import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { ControlledRadioGroup } from "~/components/form/controlled-inputs";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { updateDiscoveryState, useDiscoveryState } from "~/lib/discovery/state";
import {
  startTimeField,
  supportField,
  coachingFormatsFields,
  coachingApproachesField,
  coachingPackagesField,
  DefineUrgencyAndReadinessSchema,
} from "~/lib/schemas/coach-discovery";

// ---------------------------------------------------------------------------
// Combined schema for section 1
// ---------------------------------------------------------------------------

const Section1Schema = DefineUrgencyAndReadinessSchema.extend({
  coachingFormats: z.string().min(1, "Please select a coaching format."),
  coachingApproaches: z.array(z.string()).min(1, "Please select at least one."),
  coachingPackages: z
    .array(z.string())
    .min(1, "Please select at least one experience level."),
});

type Section1Form = z.infer<typeof Section1Schema>;

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_public/discovery/refine/")({
  component: RefineCoachingStyle,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function RefineCoachingStyle() {
  const navigate = useNavigate();
  const { form: discoveryForm } = useDiscoveryState();

  const defaultValues = useMemo(
    (): Section1Form => ({
      startTime: discoveryForm.startTime,
      support: discoveryForm.support,
      coachingFormats: discoveryForm.coachingFormats,
      coachingApproaches: discoveryForm.coachingApproaches,
      coachingPackages: discoveryForm.coachingPackages,
    }),
    [discoveryForm],
  );

  const { handleSubmit, control, watch, formState } = useForm<Section1Form>({
    resolver: zodResolver(Section1Schema),
    defaultValues,
    mode: "onTouched",
  });

  const { errors, isSubmitting } = formState;

  const watchedApproaches = watch("coachingApproaches") || [];
  const tooManyApproaches =
    watchedApproaches.length > 2 && !watchedApproaches.includes("Not sure yet");

  const onSubmit = (data: Section1Form) => {
    updateDiscoveryState(data);
    void navigate({ to: "/discovery/refine/fit" });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiscoveryStep.Title title="Coaching Style & Preferences" />

      {/* Urgency & readiness */}
      <DiscoveryStep.Card>
        <FormField
          label={startTimeField.subTitle || "When are you looking to start?"}
          labelSize="xl"
          error={errors.startTime?.message}
        >
          <ControlledRadioGroup
            control={control}
            name="startTime"
            options={
              startTimeField.options?.map(({ value, label }) => ({
                value,
                label,
              })) || []
            }
            className="flex flex-col gap-y-4"
          />
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Card>
        <FormField
          label={supportField.subTitle || "How much support do you need?"}
          labelSize="xl"
          error={errors.support?.message}
        >
          <ControlledRadioGroup
            control={control}
            name="support"
            options={
              supportField.options?.map(({ value, label, description }) => ({
                value,
                label,
                description,
              })) || []
            }
            className="flex flex-col gap-y-4"
          />
        </FormField>
      </DiscoveryStep.Card>

      {/* Coaching format & approaches */}
      <DiscoveryStep.Card>
        <FormField
          label={coachingFormatsFields.subTitle || "Preferred coaching format"}
          labelSize="xl"
          error={errors.coachingFormats?.message}
        >
          <ControlledRadioGroup
            control={control}
            name="coachingFormats"
            options={
              coachingFormatsFields.options?.map(({ value, label }) => ({
                value,
                label,
              })) || []
            }
            className="flex flex-col gap-y-4"
          />
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Card>
        <FormField
          label={coachingApproachesField.subTitle || "Coaching approach"}
          labelSize="xl"
          error={errors.coachingApproaches?.message}
        >
          <CheckboxGroupField
            name="coachingApproaches"
            control={control}
            options={coachingApproachesField.options || []}
            exclusiveOptions={["Not sure yet"]}
          />
          {tooManyApproaches && (
            <p className="mt-2 text-sm text-red-600">
              You cannot select more than 2 coaching approaches.
            </p>
          )}
        </FormField>
      </DiscoveryStep.Card>

      {/* Experience level */}
      <DiscoveryStep.Card>
        <FormField
          label={
            coachingPackagesField.subTitle ||
            "Coach experience level preference"
          }
          labelSize="xl"
          error={errors.coachingPackages?.message}
        >
          <CheckboxGroupField
            name="coachingPackages"
            control={control}
            options={coachingPackagesField.options || []}
            exclusiveOptions={["No preference"]}
          />
        </FormField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Control type="submit" loading={isSubmitting} />
    </form>
  );
}
